/*
 * Copyright (c) 2026. Tooling Team
 */

import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { EditableTableCell } from "@uhg-abyss/web/ui/DataTable";
import { LoincValuesContext } from "../../utils/contexts";
import { LOINC_OPTIONS_LIMIT } from "../../utils/constants";
import { formatCellValue } from "../../utils/formatters";

const buildLoincOption = ({ code, description }) => ({
  value: code,
  label: description ? `${code} \u2013 ${description}` : code,
});

const LoincCodeCell = ({ ...cell }) => {
  const loincValuesRef = useContext(LoincValuesContext);
  const loincValues = loincValuesRef?.current ?? [];
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const currentValue =
    cell.getValue?.() || cell.row?.original?.loincConcept?.code || "";

  const options = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();

    const matchingValues =
      term.length <= 3
        ? loincValues
        : loincValues.filter(
            ({ code, description }) =>
              code?.toLowerCase().includes(term) ||
              description?.toLowerCase().includes(term)
          );

    const initialOptions = matchingValues
      .slice(0, LOINC_OPTIONS_LIMIT)
      .map(buildLoincOption);

    if (term.length > 3 || !currentValue) return initialOptions;

    const match = loincValues.find(({ code }) => code === currentValue);
    const currentOption = buildLoincOption({
      code: currentValue,
      description: match?.description,
    });
    const remainingOptions = initialOptions.filter(
      ({ value }) => value !== currentValue
    );
    return [currentOption, ...remainingOptions];
  }, [loincValues, debouncedSearch, currentValue]);

  const editActions = cell.table?.options?.meta?.editActions;
  const isRowInEditMode = editActions?.rowsInEditMode?.[cell.row?.id];
  const displayData = cell.row?.original?.["loincConcept"]?.code;

  if (isRowInEditMode) {
    const handleLoincChange = (selectedCode) => {
      editActions?.updateTempData(
        cell.row?.index,
        "loincConceptCode",
        selectedCode
      );
      const match = loincValues.find(({ code }) => code === selectedCode);
      editActions?.updateTempData(
        cell.row?.index,
        "loincConceptDescription",
        match?.description ?? ""
      );
    };

    return (
      <EditableTableCell
        {...cell}
        getValue={() => currentValue}
        placeholder="Type to search"
        inputType="select"
        options={options}
        selectConfig={{
          isSearchable: true,
          onInputChange: setSearchInput,
          onChange: handleLoincChange,
        }}
      />
    );
  }

  return formatCellValue(displayData);
};

LoincCodeCell.propTypes = {
  getValue: PropTypes.func,
  row: PropTypes.object,
  table: PropTypes.object,
};

export default LoincCodeCell;
